/**
 * ASI Builder logo · renders the real corporate mark from
 * public/asi-logo-mark.png (or the full lockup incl. wordmark
 * from public/asi-logo-full.png).
 *
 * Both assets have transparent backgrounds so they blend onto any
 * surface tint without a white rectangle behind them.
 *
 * `size` is the pixel HEIGHT of the mark. The mark's native aspect
 * (~1.54:1) is preserved by leaving width auto.
 */
type Variant = "mark" | "full";

interface Props {
  variant?: Variant;
  size?: number;
  className?: string;
  /** Only applies to variant="full". Hides the tagline row. */
  showTagline?: boolean;
}

const MARK_ASPECT = 194 / 126;   // native mark aspect ratio
const FULL_ASPECT = 228 / 200;   // native full-lockup aspect ratio

export function AsiLogo({
  variant = "mark",
  size = 32,
  className = "",
  showTagline = true,
}: Props) {
  if (variant === "mark") {
    return (
      <img
        src="/asi-logo-mark.png"
        alt="ASI Builder"
        width={Math.round(size * MARK_ASPECT)}
        height={size}
        className={`inline-block select-none ${className}`}
        draggable={false}
      />
    );
  }

  // Full lockup — the PNG already contains "ASI Builder / AI Automation
  // Agent". If the caller wants only the wordmark hidden, they should
  // use variant="mark" instead; showTagline is preserved as a no-op
  // pass-through for API symmetry with older callsites.
  void showTagline;
  return (
    <img
      src="/asi-logo-full.png"
      alt="ASI Builder · AI Automation Agent"
      width={Math.round(size * FULL_ASPECT)}
      height={size}
      className={`inline-block select-none ${className}`}
      draggable={false}
    />
  );
}
